import type { Extension, Goal } from '@medplum/fhirtypes';

/**
 * Frontend mirror of the clinical-findings review markers for Goal — see
 * `libs/backend/mini-apps/src/lib/agents/clinical-findings/systems.ts` and
 * `finding-types/goal.ts` (the backend source of truth for these semantics).
 * The systems are environment-independent constants rooted at the
 * centralized Eluve FHIR namespace (ENG-4575), so mirroring the strings is
 * safe; the backend lib itself is not importable from frontend code.
 *
 * Unlike Condition (whose review axis is `verificationStatus`), Goal's
 * review axis is its native `lifecycleStatus`: the agent writes drafts as
 * `proposed`, accept flips to `accepted`, retract/dismiss to
 * `entered-in-error`.
 */
const ELUVE_FHIR_NAMESPACE = 'https://eluve.com';

/** meta.tag system carrying the human-review state of an AI finding. */
export const REVIEW_STATUS_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/review-status`;
export const REVIEW_STATUS_PENDING = 'pending';
/** Human approved the finding → lifecycleStatus accepted. */
export const REVIEW_STATUS_ATTESTED = 'attested';
/** Human rejected the finding → lifecycleStatus entered-in-error. */
export const REVIEW_STATUS_REJECTED = 'rejected';

/** Extension carrying the agent's 0–1 confidence in an extracted finding. */
export const FINDING_CONFIDENCE_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/finding-confidence`;

/** Retracted (dismissed / superseded) — off the goal list everywhere. */
export function isRetracted(goal: Goal): boolean {
  return goal.lifecycleStatus === 'entered-in-error';
}

/**
 * Still awaiting human review: the uniform `review-status: pending` tag, or —
 * for goals written outside the agent pipeline — a bare `proposed`
 * lifecycleStatus (the status the agent writes drafts with; the analog of
 * Condition's `provisional`). Retracted resources are never pending, even
 * when a stale pending tag lingers after a supersede.
 */
export function isPendingReview(goal: Goal): boolean {
  if (isRetracted(goal)) return false;
  if (goal.lifecycleStatus === 'proposed') return true;
  return Boolean(
    goal.meta?.tag?.some(
      (tag) =>
        tag.system === REVIEW_STATUS_SYSTEM &&
        tag.code === REVIEW_STATUS_PENDING,
    ),
  );
}

/** The supporting transcript passage stored on a finding (first note.text). */
export function readEvidence(goal: Goal): string | null {
  return goal.note?.[0]?.text ?? null;
}

/** The 0–1 confidence stamped on a finding's extension, or null. */
export function readConfidence(goal: Goal): number | null {
  const extension = (goal as { extension?: Extension[] }).extension?.find(
    (e) => e.url === FINDING_CONFIDENCE_EXTENSION,
  );
  return extension?.valueDecimal ?? null;
}

/**
 * Replace the `review-status` meta.tag (pending → attested / rejected),
 * preserving every other tag — same transition the backend reviewer applies.
 */
export function setReviewStatusTag(goal: Goal, code: string): Goal {
  const others = (goal.meta?.tag ?? []).filter(
    (tag) => tag.system !== REVIEW_STATUS_SYSTEM,
  );
  return {
    ...goal,
    meta: {
      ...goal.meta,
      tag: [...others, { system: REVIEW_STATUS_SYSTEM, code }],
    },
  };
}
